'use client'

import { Suspense, useState, type ReactNode } from 'react'
import {
  IconArrowRight,
  IconBook2,
  IconFileText,
  IconMusic,
  IconPlus
} from '@tabler/icons-react'
import { AnimatePresence, motion } from 'motion/react'
import { ProductShell } from '@/components/layout/product-shell'
import { PageLayout } from '@/components/layout/page-layout'
import { Button } from '@/components/ui/button'
import { DocumentList } from '@/components/documents/document-list'
import { StoryListClient } from '@/components/story-list-client'
import { ContentCard } from '@/components/content-card'
import { StoryRecommendationsSection } from '@/components/stories/story-recommendations-section'
import { SeriesShelf } from '@/components/stories/series-shelf'
import { useReaderContentSummaries } from '@/hooks/useReaderContents'
import { useMediaHistory } from '@/hooks/useMediaHistory'
import { Link } from '@/i18n/routing'
import { canonicalRoutes } from '@/lib/product-routes'
import { cn } from '@/lib/utils'
import { MoyuCTA } from '@/components/moyu/moyu-cta'
import type {
  StoryLearningIndexItem,
  StoryMetadata,
  StorySeries
} from '@/content/stories'

type ReaderFilter = 'stories' | 'texts' | 'lyrics'

const filters: Array<{ label: string; value: ReaderFilter }> = [
  { label: 'Stories', value: 'stories' },
  { label: 'Texts', value: 'texts' },
  { label: 'Songs', value: 'lyrics' }
]

type ReadPageClientProps = {
  heading: string
  description: string
  stories: StoryMetadata[]
  storyLearningIndex: StoryLearningIndexItem[]
  storySeriesRegistry: StorySeries[]
  allLevels: string[]
  storyTopics: string[]
}

function ReadContent({
  heading,
  description,
  stories,
  storyLearningIndex,
  storySeriesRegistry,
  allLevels,
  storyTopics
}: ReadPageClientProps) {
  const [filter, setFilter] = useState<ReaderFilter>('stories')
  const { data: lyricsItems } = useReaderContentSummaries('lyrics')
  const { data: mediaHistory = [] } = useMediaHistory('youtube')

  const showStories = filter === 'stories'
  const showTexts = filter === 'texts'
  const showLyrics = filter === 'lyrics'

  return (
    <ProductShell>
      <PageLayout
        title={heading}
        description={description}
        headerAction={
          <div className="flex flex-col items-end gap-2.5">
            <PronunciationShortcuts />
            <ReaderModeSwitch value={filter} onChange={setFilter} />
          </div>
        }
        headerActionCompact={
          <div className="flex w-full flex-col gap-3">
            <PronunciationShortcuts className="justify-start" />
            <ReaderModeSwitch
              value={filter}
              onChange={setFilter}
              className="w-full"
            />
          </div>
        }
        headerActionCompactPlacement="below"
        className="text-foreground"
      >
        <div className="mx-auto box-border flex w-full max-w-6xl min-w-0 flex-col gap-6 px-4 py-5 md:px-6 md:py-7">
          <AnimatePresence mode="wait" initial={false}>
            {showStories && (
              <ReaderModePanel key="stories">
                <MoyuCTA variant="strip" surface="story_library" />
                <StoryRecommendationsSection stories={storyLearningIndex} />
                <SeriesShelf
                  seriesRegistry={storySeriesRegistry}
                  stories={stories}
                />
                <StoryListClient
                  stories={stories}
                  allLevels={allLevels}
                  storyTopics={storyTopics}
                  getStoryHref={(story) => `/read/${story.slug}`}
                  animate={false}
                />
              </ReaderModePanel>
            )}

            {showTexts && (
              <ReaderModePanel
                key="texts"
                action={
                  <Button asChild size="sm">
                    <Link href={canonicalRoutes.readImport}>
                      <IconPlus className="h-4 w-4" />
                      Import
                    </Link>
                  </Button>
                }
              >
                <DocumentList
                  onReadDocument={(docId) => `/read/document/${docId}`}
                />
              </ReaderModePanel>
            )}

            {showLyrics && (
              <ReaderModePanel
                key="lyrics"
                action={
                  <Button asChild size="sm">
                    <Link href={canonicalRoutes.readMusic}>
                      <IconMusic className="h-4 w-4" />
                      New song
                    </Link>
                  </Button>
                }
              >
                {lyricsItems?.length || mediaHistory.length ? (
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {(lyricsItems ?? []).map((item) => (
                      <ContentCard
                        key={item.id}
                        label="Song"
                        title={item.title}
                        subtitle={item.subtitle ?? 'Lyrics'}
                        actionHref={`/read/lyrics/${item.id}`}
                        actionLabel="Read"
                        className="h-full"
                      />
                    ))}
                    {mediaHistory
                      .filter(
                        (item) =>
                          !item.reader_content_id ||
                          !(lyricsItems ?? []).some(
                            (lyrics) => lyrics.id === item.reader_content_id
                          )
                      )
                      .map((item) => (
                        <ContentCard
                          key={item.id}
                          label="YouTube"
                          title={item.title ?? 'Saved YouTube song'}
                          subtitle={item.subtitle ?? 'Link saved'}
                          actionHref={
                            item.reader_content_id
                              ? `/read/lyrics/${item.reader_content_id}`
                              : canonicalRoutes.readMusic
                          }
                          actionLabel={
                            item.reader_content_id ? 'Read' : 'Find lyrics'
                          }
                          className="h-full"
                        />
                      ))}
                  </div>
                ) : (
                  <ReaderEmptyState>
                    Songs you open will appear here.
                  </ReaderEmptyState>
                )}
              </ReaderModePanel>
            )}
          </AnimatePresence>
        </div>
      </PageLayout>
    </ProductShell>
  )
}

function PronunciationShortcuts({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'text-muted-foreground flex flex-wrap items-center justify-end gap-x-4 gap-y-1.5 text-sm',
        className
      )}
    >
      <span className="text-xs font-medium">Pronunciation</span>
      <Link
        href={canonicalRoutes.pinyin}
        className="text-foreground hover:text-primary group inline-flex items-center gap-1 font-semibold transition-colors"
      >
        Learn Pinyin
        <IconArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </Link>
      <Link
        href={canonicalRoutes.zhuyin}
        className="text-foreground hover:text-primary group inline-flex items-center gap-1 font-semibold transition-colors"
      >
        Learn Zhuyin
        <IconArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
  )
}

function ReaderModeSwitch({
  value,
  onChange,
  compact = false,
  className
}: {
  value: ReaderFilter
  onChange: (value: ReaderFilter) => void
  compact?: boolean
  className?: string
}) {
  const [hoveredFilter, setHoveredFilter] = useState<ReaderFilter | null>(null)

  return (
    <div
      className={cn(
        'relative grid grid-cols-3 gap-1 overflow-hidden rounded-full',
        compact ? 'w-[9.5rem]' : 'w-[22rem]',
        className
      )}
      aria-label="Reader mode"
      onMouseLeave={() => setHoveredFilter(null)}
      role="tablist"
    >
      {filters.map((item) => {
        const isActive = value === item.value

        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={item.label}
            onMouseEnter={() => setHoveredFilter(item.value)}
            onClick={() => onChange(item.value)}
            className={cn(
              'relative isolate inline-flex h-10 items-center justify-center gap-2 rounded-full border border-transparent px-3 text-sm font-semibold transition-colors duration-200',
              isActive
                ? 'text-foreground'
                : 'text-muted-foreground hover:bg-muted/35 hover:text-foreground',
              compact && 'px-2'
            )}
          >
            <AnimatePresence>
              {(hoveredFilter === item.value ||
                (!hoveredFilter && isActive)) && (
                <motion.span
                  layoutId={
                    compact
                      ? 'reader-filter-pill-compact'
                      : 'reader-filter-pill'
                  }
                  className="bg-card ring-border/60 absolute inset-0 -z-10 rounded-full shadow-xs ring-1"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 420,
                    damping: 34,
                    mass: 0.7
                  }}
                />
              )}
            </AnimatePresence>
            <ReaderModeIcon value={item.value} />
            {!compact && <span>{item.label}</span>}
          </button>
        )
      })}
    </div>
  )
}

function ReaderModeIcon({ value }: { value: ReaderFilter }) {
  const Icon =
    value === 'stories'
      ? IconBook2
      : value === 'texts'
        ? IconFileText
        : IconMusic

  return <Icon className="h-4 w-4" />
}

function ReaderEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="text-muted-foreground bg-muted/20 rounded-lg border border-dashed p-10 text-center text-sm">
      {children}
    </div>
  )
}

function ReaderModePanel({
  action,
  children
}: {
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <motion.section
      className="min-w-0 space-y-6"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
    >
      {action && <div className="flex min-w-0 justify-end">{action}</div>}
      {children}
    </motion.section>
  )
}

function ReadPageFallback({
  heading,
  description
}: Pick<ReadPageClientProps, 'heading' | 'description'>) {
  return (
    <ProductShell>
      <PageLayout title={heading} description={description}>
        <div className="text-muted-foreground flex min-h-[50dvh] items-center justify-center">
          Loading reader...
        </div>
      </PageLayout>
    </ProductShell>
  )
}

export function ReadPageClient(props: ReadPageClientProps) {
  return (
    <Suspense
      fallback={
        <ReadPageFallback
          heading={props.heading}
          description={props.description}
        />
      }
    >
      <ReadContent {...props} />
    </Suspense>
  )
}
